import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from '@/hooks/useApi';
import { useWishlist } from '@/hooks/useWishlist';
import { apiProductToProduct } from '@/services/adapters';
import { useCart } from '@/hooks/useCart';

export default function WishlistPage() {
  const { wishlist, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const { data: apiProducts, loading } = useProducts({});

  const products = useMemo(() => {
    if (!apiProducts?.data) return [];
    return apiProducts.data
      .filter(p => wishlist.includes(String(p.id)))
      .map(p => apiProductToProduct(p));
  }, [apiProducts, wishlist]);

  return (
    <main className="min-h-screen">
      <div className="bg-cream-200 py-12 md:py-16">
        <div className="container-main text-center">
          <h1 className="heading-xl mb-3">المفضلة</h1>
          <p className="body-text">
            {wishlist.length > 0
              ? `${wishlist.length} منتج في قائمة المفضلة`
              : 'لم تضيفي أي منتج للمفضلة بعد'}
          </p>
        </div>
      </div>

      <section className="section-padding container-main">
        {loading ? (
          <div className="text-center py-20">
            <div className="animate-pulse w-16 h-16 bg-cream-200 rounded-full mx-auto mb-4" />
            <p className="text-charcoal-400">جاري التحميل...</p>
          </div>
        ) : products.length > 0 ? (
          <>
            <p className="text-sm text-charcoal-500 mb-6">{products.length} منتج</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map(product => (
                <div key={product.id} className="group relative bg-white rounded-2xl border border-cream-300 overflow-hidden hover:shadow-lg transition-all duration-300">
                  {/* Remove button */}
                  <button
                    onClick={() => removeFromWishlist(product.id)}
                    className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center text-red-500 hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="حذف من المفضلة"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>

                  {/* Image */}
                  <Link to={`/product/${product.slug}`} className="block relative aspect-[3/4] overflow-hidden bg-cream-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {product.badge && (
                      <span className="absolute top-3 left-3 bg-blush-200 text-charcoal-900 text-[10px] font-bold px-2.5 py-1 rounded-full">
                        {product.badge}
                      </span>
                    )}
                  </Link>

                  {/* Info */}
                  <div className="p-4">
                    <p className="text-xs text-charcoal-400 mb-1">{product.category}</p>
                    <Link to={`/product/${product.slug}`} className="block">
                      <h3 className="text-sm font-semibold text-charcoal-900 mb-2 line-clamp-2 hover:text-blush-200 transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    <div className="flex items-center justify-between">
                      <div>
                        {product.oldPrice && (
                          <span className="text-xs text-charcoal-400 line-through ml-2">{product.oldPrice} شيكل</span>
                        )}
                        <span className="text-sm font-bold text-charcoal-900">{product.price} شيكل</span>
                      </div>
                    </div>
                    <button
                      onClick={() => addToCart(product, 1)}
                      className="w-full mt-3 py-2 bg-charcoal-900 text-white text-xs font-medium rounded-lg hover:bg-charcoal-800 transition-colors"
                    >
                      أضف للسلة
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-cream-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-heart-line text-3xl text-charcoal-400" />
            </div>
            <h2 className="text-lg font-bold text-charcoal-900 mb-2">المفضلة فارغة</h2>
            <p className="text-sm text-charcoal-500 mb-6">أضيفي منتجاتك المفضلة لتجديها بسهولة</p>
            <Link to="/shop" className="btn-primary inline-block">
              تصفحي المنتجات
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}
